<template>
    <v-form v-model="planValid">
        <fe-label>Intervention Group Label</fe-label>
        <v-text-field flat solo dense
            v-model="plan.intervention_plan_name"
            :rules="$fieldValidators('text', null, { required: true, limit: 255})"
            :counter="255"
        />

        <fe-remote-combo
            :label="featureFlagMultipleInterventionists ? 'Primary Interventionist' : 'Interventionist'"
            itemValue="id"
            itemText="user_full_name"
            :items="interventionists"
            :multiple="false"
            byId
            ref="interventionist"
            v-model="plan.intervention_plan_user_id"
            :rules="$fieldValidators('select', null, { required: true })"
            @change="changePrimaryInterventionist"
        />
        <a
            v-if="featureFlagMultipleInterventionists && !showAdditionalInterventionsts"
            @click="showAdditionalInterventionsts = true"
            style="position:relative;left:20px;top:-15px;"
        >
            <v-icon size="12" color="#006c90" style="position:relative;top:-2px;padding-right:10px;">fas fa-plus</v-icon> Add Interventionists
        </a>

        <div v-if="featureFlagMultipleInterventionists && showAdditionalInterventionsts">
            <fe-label>
                Additional Interventionists (Optional)
                <fe-info-tip
                    class="ml-2"
                    style="display:inline-block;"
                    tooltip="You can add up to 5 additional interventionists."
                />
            </fe-label>
            <fe-remote-combo
                v-model="plan.secondary_interventionists"
                itemText="user_full_name"
                itemValue="id"
                byId
                chips multiple
                :clearable="false"
                :items="filteredSecondaryInterventionists"
                :selectAllOption="false"
                :rules="secondaryInterventionistValidation"
            />
        </div>

        <fe-remote-combo
            v-if="categoryId !== 4"
            label="Level"
            :url="$models.getUrl('interventionLevel', 'read')+'&active=1'"
            rootProperty="intervention_levels"
            itemValue="id"
            itemText="name"
            :multiple="false"
            byId
            ref="interventionLevel"
            v-model="plan.intervention_level_id"
            :rules="$fieldValidators('select', null, { required: true })"
        />

        <fe-remote-combo
            v-if="categoryId !== 4"
            label="Strategy"
            :items="strategies"
            :multiple="false"
            byId
            ref="interventionStrategy"
            v-model="plan.intervention_type_id"
            :rules="$fieldValidators('select', null, { required: true })"
        />

        <periods-expectations
            v-if="showPeriodsExpectations && !lockPeriodsExpectations"
            v-model="pointSheet"
            :strategy="strategySummary"
        />
        <div v-if="lockPeriodsExpectations" class="caption" style="color: gray;">
            This plan already has progress monitoring data, so Periods and Expectations can no longer be edited. Please make a new phase or plan.
        </div>

        <fe-label v-if="categoryId && categoryId !== 0">Phase Name</fe-label>
        <v-text-field v-if="categoryId && categoryId !== 0" flat solo dense
            v-model="plan.intervention_plan_phase_name"
            :rules="$fieldValidators('text', null, {limit: 255})"
            :counter="255"
        />

        <div v-if="categoryId && categoryId !== 0" style="font-size: 12px; font-weight: bold;">
            <v-icon>fal fa-info-circle</v-icon> This will update the current phase name.  This will not add a phase.
        </div>

        <div class="d-flex justify-end">
            <fe-btn usage="primary" @click="modifyPlan" :disabled="!planValid">Modify</fe-btn>
        </div>
    </v-form>
</template>

<script>
    import PeriodsExpectations from './PeriodsExpectations'

    export default {
        name: 'PlanDetails',
        components: { PeriodsExpectations },
        props: {
            value: {
                default: () => { return {} }
            },
            interventionPlanId: {
                required: true
            },
            showTools: {
                type: Boolean,
                default: true
            },
            studentsString: {
                type: String,
                default: null
            },
        },
        computed: {
            filteredSecondaryInterventionists() {
                // don't show the primary interventionist
                const primaryInterventionist = this.plan.intervention_plan_user_id
                return this.interventionists.filter(interventionist => interventionist.id !== primaryInterventionist)
            },
            secondaryInterventionistValidation: () => [
                v => v.length <= 5 || 'You can add up to 5 secondary interventionists.'
            ],
            featureFlagMultipleInterventionists () {
                return this.$store.getters['flags/flags']['ec-multiple-interventionists'] === true
            },
            selectedIntvType() {
                return this.strategies.find(t => t.id === this.plan.intervention_type_id)
            },
            showPeriodsExpectations() {
                return this.selectedIntvType?.monitor_category_id === 2
            },
            strategySummary() {
                return this.plan.intervention_type_id ? { id: this.plan.intervention_type_id } : null
            },
            lockPeriodsExpectations() {
                return !!this.plan.has_progress_monitoring_data
            }
        },
        data() {
            return {
                plan: {},
                planValid: false,
                showAdditionalInterventionsts: false,
                interventionists: [],
                strategies: [],
                categoryId: null,
                pointSheet: { monitors: [] },
            }
        },
        watch: {
            value(v) {
                this.plan = {...v}
            },
            planValid(v) {
                this.$emit('valid', v)
            },
            pointSheet: {
                deep: true,
                handler(v) {
                    if (this.showPeriodsExpectations) {
                        this.plan.monitors = [{ monitor_score_options: v.monitors }]
                    }
                }
            },
            showPeriodsExpectations(v) {
                if (!v) {
                    delete this.plan.monitors
                }
            }
        },
        mounted() {
            this.load()
        },
        methods: {
            load() {
                this.$modelGet('interventionPlanStudent', { intervention_plan_id: this.interventionPlanId, include_monitor_info: 1 }).then(r => {
                    if (r.length) {
                        this.plan = {
                            intervention_plan_id: r[0].intervention_plan_id,
                            intervention_plan_name: r[0].intervention_plan_name,
                            intervention_plan_user_id: r[0].intervention_plan_user_id,
                            secondary_interventionists: r[0].interventionists.slice(1).map(interventionist => interventionist.user_id),
                            intervention_type_id: r[0].intervention_type_id,
                            intervention_level_id: r[0].intervention_level_id,
                            intervention_plan_phase_name: r[0].intervention_plan_phase_name,
                            has_progress_monitoring_data: r.some(r => r.monitor_score_count > 0),
                        }

                        this.categoryId = r[0].category_id
                        this.pointSheet = { monitors: r[0].monitor_score_options || [] }

                        // show additional interventionists if any are selected
                        this.showAdditionalInterventionsts = this.plan.secondary_interventionists.length > 0

                        this.$modelGet('interventionType', { active: 1, category_id: r[0].category_id, student_id: this.studentsString, school_year_id: r[0].school_year_id })
                            .then(s => {
                                this.strategies = s
                            })

                        // load interventionists
                        this.$axios.get(this.$models.getUrl('user', 'read') + '&active=1')
                            .then(response => {
                                this.interventionists = response.data
                            })
                    } else {
                        this.plan = {}
                    }
                })
            },
            modifyPlan() {
                this.$messageBox({
                    title: 'Override Plan?',
                    persistent: true,
                    maxWidth: 600,
                    message: 'Saving this record will update the entire intervention plan record for all students.  If you are trying to create a new phase, select <b>New Phase</b> from the previous menu.',
                    actions: [{
                        text: 'Cancel', primary: false
                    }, {
                        text: 'Override', primary: true,
                        onClick: () => {
                            this.$axios.post('interventionPlan.php?action=update&allow_new_plan=0&property=plan&confirm=1', this.plan)
                                .then(response => {
                                    this.$ecResponse(response)
                                    this.$emit('save', true)
                                })
                        }
                    }]
                })
            },
            changePrimaryInterventionist(primaryInterventionist) {
                // remove selected primary interventionist from secondary interventionists if present
                this.plan.secondary_interventionists = this.plan.secondary_interventionists.filter(
                    secondaryInterventionist => secondaryInterventionist !== primaryInterventionist?.id
                )
            }
        }
    }
</script>

<style lang="scss" scoped>

</style>